/**
 * Keeps the reseller's setup between visits (Martin: skip now, come back later, land where you left off).
 * Browser storage per signed-in user until tenant-core stores it server-side; every access is wrapped
 * because storage can be blocked or full. Payment credentials and bank details are deliberately NOT
 * stored here: secrets do not belong in localStorage, so that step asks again after a reload.
 */
import { INITIAL_SETUP, type SetupState, type StepId } from './setupState';

export type SetupStatus = 'started' | 'draft' | 'live';
export interface SavedSetup { s: SetupState; step: StepId; reached: number; status: SetupStatus }

const VERSION = 1;

function key(): string {
  let uid = '';
  try { uid = localStorage.getItem('modus_current_user_id') || ''; } catch { /* storage blocked */ }
  return `tc_whitelabel_setup_v${VERSION}${uid ? `:${uid}` : ''}`;
}

export function loadSetup(): SavedSetup | null {
  try {
    const raw = localStorage.getItem(key());
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (d?.v !== VERSION || !d.s) return null;
    const s: SetupState = {
      ...INITIAL_SETUP,
      ...d.s,
      services: { ...INITIAL_SETUP.services, ...d.s.services },
      extras: { ...INITIAL_SETUP.extras, ...d.s.extras },
      pay: { ...INITIAL_SETUP.pay, ...d.s.pay },
      customPay: { ...INITIAL_SETUP.customPay, ...d.s.customPay },
      business: { ...INITIAL_SETUP.business, ...d.s.business },
      balance: { alert: d.s.balance?.alert ?? INITIAL_SETUP.balance.alert, wallets: { ...INITIAL_SETUP.balance.wallets, ...d.s.balance?.wallets } },
      affiliate: { ...INITIAL_SETUP.affiliate, ...d.s.affiliate },
      mail: { ...INITIAL_SETUP.mail, ...d.s.mail },
      legal: { ...INITIAL_SETUP.legal, ...d.s.legal },
      hiddenPkgs: Array.isArray(d.s.hiddenPkgs) ? d.s.hiddenPkgs : INITIAL_SETUP.hiddenPkgs,
      coupons: Array.isArray(d.s.coupons) ? d.s.coupons : INITIAL_SETUP.coupons,
      levels: Array.isArray(d.s.levels) ? d.s.levels : INITIAL_SETUP.levels,
      team: Array.isArray(d.s.team) ? d.s.team : INITIAL_SETUP.team,
      prices: { ...d.s.prices },
      offered: Array.isArray(d.s.offered) ? d.s.offered : INITIAL_SETUP.offered,
      details: { ...INITIAL_SETUP.details },
    };
    return { s, step: d.step ?? 'brand', reached: Number(d.reached) || 0, status: d.status ?? 'started' };
  } catch {
    return null;
  }
}

export function saveSetup(saved: SavedSetup, toServer = true): void {
  const { details: _secrets, ...rest } = saved.s;
  try { localStorage.setItem(key(), JSON.stringify({ v: VERSION, ...saved, s: rest })); } catch { /* storage full or blocked */ }
  if (toServer) push(saved);
}

export function clearSetup(): void {
  try { localStorage.removeItem(key()); } catch { /* storage blocked */ }
}

/** Saves setup content from outside the wizard (the reseller admin) without moving the wizard's place. */
export function saveSetupState(s: SetupState): void {
  const prev = loadSetup();
  saveSetup({ s, step: prev?.step ?? 'brand', reached: prev?.reached ?? 0, status: prev?.status ?? 'started' });
}

// ── Server (backend routers/reseller.py) ─────────────────────────────────────
// Logged in on our site (isLiveAdmin), the shop lives on the server: this browser copy is only a cache so pages
// can render synchronously. Loading pulls the server state in; every save is pushed back, debounced.

import { ApiError, isLiveAdmin, resellerApi, type ServerTenant } from '../api';
import type { PayDetails } from './setupState';

// Fields the server keeps (backend tenants/config.py DEFAULT_CONFIG). Affiliate, levels and team are not in v1.
const SERVER_KEYS = ['name', 'logo', 'color', 'services', 'extras', 'markup', 'prices', 'offered', 'expert', 'hiddenPkgs', 'mode',
  'domain', 'pay', 'customPay', 'business', 'terms', 'coupons', 'balance', 'mail', 'legal'] as const;

let server: ServerTenant | null = null;
const listeners = new Set<(t: ServerTenant) => void>();
const notify = () => { if (server) listeners.forEach((fn) => fn(server!)); };

export const serverTenant = () => server;
export function onServerTenant(fn: (t: ServerTenant) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function toServerConfig(s: SetupState): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of SERVER_KEYS) out[k] = (s as unknown as Record<string, unknown>)[k];
  return out;
}

function fromServer(t: ServerTenant): SavedSetup {
  const c = t.config as Partial<SetupState>;
  const s: SetupState = {
    ...INITIAL_SETUP,
    ...c,
    services: { ...INITIAL_SETUP.services, ...(c.services || {}) },
    extras: { ...INITIAL_SETUP.extras, ...(c.extras || {}) },
    pay: { ...INITIAL_SETUP.pay, ...(c.pay || {}) },
    customPay: { ...INITIAL_SETUP.customPay, ...(c.customPay || {}) },
    business: { ...INITIAL_SETUP.business, ...(c.business || {}) },
    balance: { alert: c.balance?.alert ?? INITIAL_SETUP.balance.alert, wallets: { ...INITIAL_SETUP.balance.wallets, ...(c.balance?.wallets || {}) } },
    mail: { ...INITIAL_SETUP.mail, ...(c.mail || {}) },
    legal: { ...INITIAL_SETUP.legal, ...(c.legal || {}) },
    logo: (c.logo as string | null | undefined) ?? undefined,
    details: { ...INITIAL_SETUP.details },
  };
  const status: SetupStatus = t.status === 'live' ? 'live' : t.wizard.reached > 0 ? 'draft' : 'started';
  return { s, step: (t.wizard.step as StepId) || 'brand', reached: t.wizard.reached || 0, status };
}

/** Pulls the shop from the server into the cache. Returns null outside the live admin or when it fails. */
export async function hydrateFromServer(): Promise<SavedSetup | null> {
  if (!isLiveAdmin()) return null;
  try {
    server = await resellerApi.tenant();
    const saved = fromServer(server);
    const prevStatus = loadSetup()?.status;
    // A shop that was never touched on the server keeps a started setup from this browser.
    if (!server.wizard.reached && !(server.config as { name?: string }).name && loadSetup()?.s.name) return loadSetup();
    saveSetup({ ...saved, status: saved.status === 'started' && prevStatus === 'draft' ? 'draft' : saved.status }, false);
    notify();
    return loadSetup();
  } catch {
    return null;
  }
}

let timer: ReturnType<typeof setTimeout> | undefined;
let pending: SavedSetup | null = null;
export let lastSaveError: string | null = null;

function push(saved: SavedSetup): void {
  if (!isLiveAdmin()) return;
  pending = saved;
  clearTimeout(timer);
  timer = setTimeout(async () => {
    const p = pending; pending = null;
    if (!p) return;
    try {
      server = await resellerApi.saveTenant(toServerConfig(p.s), { step: p.step, reached: p.reached });
      lastSaveError = null;
      notify();
    } catch (e) {
      lastSaveError = e instanceof ApiError ? e.message : 'Could not save. Check your connection.';
    }
  }, 600);
}

/** Sends the payment details typed in the wizard or admin (never kept in the browser). Empty fields are skipped. */
export async function saveSecrets(fields: Partial<PayDetails> & Record<string, string | undefined>): Promise<ServerTenant | null> {
  if (!isLiveAdmin()) return null;
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(fields)) if (typeof v === 'string' && v.trim()) clean[k] = v.trim();
  if (!Object.keys(clean).length) return server;
  server = await resellerApi.saveSecrets(clean);
  notify();
  return server;
}

/** Takes the shop live on the server; throws ApiError with `missing` when something is still needed. */
export async function goLiveOnServer(): Promise<ServerTenant | null> {
  if (!isLiveAdmin()) return null;
  clearTimeout(timer);
  const p = pending ?? loadSetup();
  pending = null;
  if (p) server = await resellerApi.saveTenant(toServerConfig(p.s), { step: p.step, reached: p.reached });
  server = await resellerApi.goLive();
  notify();
  return server;
}
